// RowCard — shared mobile card primitive for the responsive
// "table-on-desktop, cards-on-narrow" pattern. Provides three slots:
// a header (typically a title or badge cluster), a top-right action area
// (kebab menus, status pills, etc.), and a list of labelled fields. The
// card itself is keyboard-activatable when `onSelect` is supplied.
import { useId, type CSSProperties, type ReactNode } from 'react';

/**
 * @aiPurpose A `{ label, value }` pair rendered as one row inside RowCard's
 *   `<dl>` body. The label is shown in a muted color; the value flows right
 *   and wraps via `overflowWrap: anywhere` to survive long unbreakable tokens.
 * @aiInvariant none
 */
export interface RowCardField {
  label: string;
  value: ReactNode;
}

/**
 * @aiPurpose Public props for the RowCard primitive. See the component TSDoc
 *   for the slot semantics and accessibility contract.
 * @aiInvariant none
 */
export interface RowCardProps {
  /** Optional title/header row content. Rendered before fields, left aligned. */
  header?: ReactNode;
  /** Optional top-right action area. Must `stopPropagation` on clicks. */
  actions?: ReactNode;
  /** Labelled body rows. Empty list is allowed when header carries all info. */
  fields?: RowCardField[];
  /** Optional footer slot, rendered after fields. */
  footer?: ReactNode;
  /** Click + keyboard activation. Omit for read-only cards. */
  onSelect?: () => void;
  /** Accessible name; falls back to the header text when omitted. */
  ariaLabel?: string;
  /** Visual selected state (left accent border). */
  selected?: boolean;
  /** Visual archived state (reduced opacity). */
  archived?: boolean;
  /** Optional style override (rarely needed). */
  style?: CSSProperties;
}

/**
 * @aiPurpose Shared mobile card with header/actions/fields/footer slots. The
 *   `<article>` is keyboard-activatable when `onSelect` is supplied (Enter or
 *   Space). The actions wrapper stops click + keydown propagation so action
 *   controls don't double-fire the card's onSelect.
 * @aiInvariant Lint treats `<article>` as non-interactive, so the article+
 *   onClick and article+tabIndex rules are suppressed across the JSX block.
 *   Static-element-interactions is suppressed on the actions wrapper for the
 *   same reason; both rules are reset by matching `eslint-enable`.
 */
export function RowCard({
  header,
  actions,
  fields,
  footer,
  onSelect,
  ariaLabel,
  selected,
  archived,
  style,
}: RowCardProps) {
  const clickable = !!onSelect;
  const labelId = useId();
  return (
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
    <article
      aria-label={ariaLabel}
      aria-labelledby={ariaLabel ? undefined : header ? labelId : undefined}
      tabIndex={clickable ? 0 : -1}
      onClick={onSelect}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect?.();
              }
            }
          : undefined
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: 12,
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: 'var(--bg-1)',
        cursor: clickable ? 'pointer' : 'default',
        outline: 'none',
        opacity: archived ? 0.75 : 1,
        boxShadow: selected ? 'inset 3px 0 0 var(--accent-1)' : 'none',
        transition: 'box-shadow var(--dur-1) var(--ease-out)',
        minWidth: 0,
        overflow: 'hidden',
        ...style,
      }}
      onFocus={(e) => {
        if (clickable) e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-ring)';
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = selected ? 'inset 3px 0 0 var(--accent-1)' : 'none';
      }}
    >
      {(header || actions) && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          {header && (
            <div id={ariaLabel ? undefined : labelId} style={{ flex: 1, minWidth: 0 }}>
              {header}
            </div>
          )}
          {actions && (
            // WHY: action area children stop their own propagation; the wrapper
            // is a belt-and-suspenders guard for callers that forget. Static
            // div + stopPropagation is intentional — the actual interactive
            // children carry their own roles and keyboard handlers.
            /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
            <div
              style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {actions}
            </div>
          )}
        </div>
      )}
      {fields && fields.length > 0 && (
        <dl
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            margin: 0,
            fontSize: 13,
          }}
        >
          {fields.map((f, i) => (
            <div
              key={`${f.label}-${i}`}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 12,
              }}
            >
              <dt style={{ color: 'var(--fg-3)', fontSize: 11, flexShrink: 0 }}>{f.label}</dt>
              <dd
                style={{
                  margin: 0,
                  color: 'var(--fg-1)',
                  textAlign: 'right',
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                }}
              >
                {f.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {footer}
    </article>
    /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */
  );
}
